// Post-send outcome capture.
//
// Two surfaces:
//   1. <LogOutcomeStrip>   — a one-liner that lives below the FreeMessageCard.
//      No heading, no terracotta border, no competing button. Just a small
//      summary or a tiny "Tell us how it went →" link. The whole point is
//      to NOT compete with the firm-message card visually — the form lives
//      in the modal.
//   2. <LogOutcomeModal>   — the form, in a proper dialog. Mounted by the
//      strip when the user clicks the inline link, OR auto-opened when the
//      case-detail route receives `?log=outcome` (the deep link sent in
//      the 24h follow-up email).
//
// Why split:
//   - The previous heavy "How did it go?" banner read as a second CTA on
//     the same card and competed with the letter itself.
//   - The follow-up email needs a deep-link landing target. Modal is the
//     right surface — opens regardless of where the user is in the case
//     detail layout.

import { useEffect, useState } from 'react';
import {
  type CaseArtifact,
  type ContractorResponseKind,
  useArtifacts,
  useLogArtifactOutcome,
} from '../artifacts';
import { m } from '../paraglide/messages';

// ── Strip (inline, non-competing) ──────────────────────────────────────────

export function LogOutcomeStrip({
  caseId,
  forceOpen = false,
  onCloseAfterOpen,
}: {
  caseId: string;
  /** Set true via the case-detail route's `?log=outcome` deep link to
   * auto-open the modal on mount. */
  forceOpen?: boolean;
  /** Called after the auto-opened modal closes, so the route can clear
   * the search param. */
  onCloseAfterOpen?: () => void;
}) {
  const artifacts = useArtifacts(caseId);
  const latest = (artifacts.data ?? []).find((a) => a.kind === 'firm_message');
  const [open, setOpen] = useState(false);

  // If the route asked us to open + we have a latest artifact, fire once.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only react to forceOpen → open transition; latest re-renders are fine
  useEffect(() => {
    if (forceOpen && latest && !open) setOpen(true);
  }, [forceOpen, latest?.id]);

  if (!latest || latest.status !== 'ready') return null;

  const close = () => {
    setOpen(false);
    if (forceOpen) onCloseAfterOpen?.();
  };

  return (
    <>
      {latest.response_logged_at ? (
        <LoggedLine latest={latest} onEdit={() => setOpen(true)} />
      ) : (
        <UnloggedLine onClick={() => setOpen(true)} />
      )}
      <LogOutcomeModal caseId={caseId} latest={latest} open={open} onClose={close} />
    </>
  );
}

function UnloggedLine({ onClick }: { onClick: () => void }) {
  return (
    <p className="outcome-strip outcome-strip--unlogged small">
      <button type="button" className="link-button" onClick={onClick}>
        {m.outcomeStripUnlogged()}
      </button>
    </p>
  );
}

function LoggedLine({ latest, onEdit }: { latest: CaseArtifact; onEdit: () => void }) {
  const when = latest.response_logged_at ? formatLogged(latest.response_logged_at) : '';
  const summary = summaryFor(latest.contractor_response_kind, when);
  return (
    <p className="outcome-strip outcome-strip--logged small">
      <span className="muted" aria-hidden="true">
        ✓
      </span>{' '}
      <span>{summary}</span>{' '}
      <button type="button" className="link-button" onClick={onEdit}>
        {m.outcomeBannerEdit()}
      </button>
    </p>
  );
}

// ── Modal ──────────────────────────────────────────────────────────────────

export function LogOutcomeModal({
  caseId,
  latest,
  open,
  onClose,
}: {
  caseId: string;
  latest: CaseArtifact;
  open: boolean;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: Esc handling is wired via window listener
    <dialog
      className="modal-backdrop"
      open
      aria-label={m.outcomeBannerHeading()}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-shell outcome-modal">
        <header className="outcome-modal-head">
          <div className="outcome-modal-title-block">
            <h2 className="outcome-modal-title">{m.outcomeBannerHeading()}</h2>
            <p className="outcome-modal-subtitle">{m.outcomeBannerBody()}</p>
          </div>
          <button type="button" className="btn-ghost btn-xs" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>
        <OutcomeForm caseId={caseId} latest={latest} onDone={onClose} />
      </div>
    </dialog>
  );
}

// ── Form (renders inside the modal) ────────────────────────────────────────

function OutcomeForm({
  caseId,
  latest,
  onDone,
}: {
  caseId: string;
  latest: CaseArtifact;
  onDone: () => void;
}) {
  const log = useLogArtifactOutcome(caseId);
  const [sentAt, setSentAt] = useState<string>(toDateInput(latest.sent_at));
  const [kind, setKind] = useState<ContractorResponseKind | ''>(
    latest.contractor_response_kind ?? '',
  );
  const [note, setNote] = useState<string>(latest.contractor_response_note ?? '');

  function submit(e: React.FormEvent) {
    e.preventDefault();
    log.mutate(
      {
        artifactId: latest.id,
        sent_at: sentAt ? new Date(sentAt).toISOString() : null,
        contractor_response_kind: (kind || null) as ContractorResponseKind | null,
        contractor_response_note: note.trim() ? note.trim() : null,
      },
      { onSuccess: () => onDone() },
    );
  }

  return (
    <form className="outcome-form" onSubmit={submit}>
      <label className="outcome-form-row">
        <span className="outcome-form-label">{m.outcomeFormSentAtLabel()}</span>
        <input
          type="date"
          value={sentAt}
          onChange={(e) => setSentAt(e.target.value)}
          placeholder={m.outcomeFormSentAtPlaceholder()}
          className="outcome-form-input"
        />
      </label>
      <fieldset className="outcome-form-row outcome-form-fieldset">
        <legend className="outcome-form-label">{m.outcomeFormResponseLabel()}</legend>
        <RadioOption
          value="no_reply"
          checked={kind === 'no_reply'}
          onChange={setKind}
          label={m.outcomeResponseNoReply()}
        />
        <RadioOption
          value="partial_reply"
          checked={kind === 'partial_reply'}
          onChange={setKind}
          label={m.outcomeResponsePartial()}
        />
        <RadioOption
          value="agreed_to_fix"
          checked={kind === 'agreed_to_fix'}
          onChange={setKind}
          label={m.outcomeResponseAgreed()}
        />
        <RadioOption
          value="refused"
          checked={kind === 'refused'}
          onChange={setKind}
          label={m.outcomeResponseRefused()}
        />
        <RadioOption
          value="other"
          checked={kind === 'other'}
          onChange={setKind}
          label={m.outcomeResponseOther()}
        />
      </fieldset>
      <label className="outcome-form-row">
        <span className="outcome-form-label">{m.outcomeFormNoteLabel()}</span>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="outcome-form-textarea"
          rows={3}
          maxLength={2000}
        />
      </label>
      <div className="outcome-form-actions">
        <button type="button" className="btn-ghost" onClick={onDone} disabled={log.isPending}>
          {m.outcomeFormCancel()}
        </button>
        <button type="submit" className="btn-primary" disabled={log.isPending}>
          {log.isPending ? m.outcomeFormSubmitting() : m.outcomeFormSubmit()}
        </button>
      </div>
    </form>
  );
}

function RadioOption({
  value,
  checked,
  onChange,
  label,
}: {
  value: ContractorResponseKind;
  checked: boolean;
  onChange: (v: ContractorResponseKind) => void;
  label: string;
}) {
  return (
    <label className="outcome-form-radio">
      <input
        type="radio"
        name="contractor_response_kind"
        value={value}
        checked={checked}
        onChange={() => onChange(value)}
      />
      <span>{label}</span>
    </label>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────

function toDateInput(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return '';
  }
}

function formatLogged(iso: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}

function summaryFor(kind: ContractorResponseKind | null, when: string): string {
  switch (kind) {
    case 'agreed_to_fix':
      return m.outcomeFormSummaryAgreed({ when });
    case 'refused':
      return m.outcomeFormSummaryRefused({ when });
    case 'partial_reply':
      return m.outcomeFormSummaryPartial({ when });
    case 'no_reply':
      return m.outcomeFormSummaryNoReply({ when });
    default:
      return m.outcomeFormSummaryOther({ when });
  }
}
